import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShoppingBag, ExternalLink, ArrowRight } from "lucide-react";
import PageShell from "@/components/PageShell";
import PageHeader from "@/components/PageHeader";
import EmailCapture from "@/components/EmailCapture";
import PromoRibbon, { withCoupon } from "@/components/PromoRibbon";
import TaggedOutbound from "@/components/TaggedOutbound";
import { digitalProducts } from "@/data/enrichments";
import { getApp, getFilms } from "@/lib/catalog";
import { activeOffer } from "@/data/offer";
import { goPath } from "@/lib/golinks";
import { openCheckout } from "@/lib/lemonsqueezy";
import { trackEvent } from "@/lib/tracking";
import { addUtm } from "@/lib/utm";

// External-storefront button — outbound link with UTM stamping + click
// tracking. Same pattern as the book-page StorefrontButton, just adapted
// for shop print products (Etsy / Gelato instead of Amazon / Google Play).
function StorefrontButton({ product, sf, index }) {
  const comingSoon = !sf.url;
  if (comingSoon) {
    return (
      <span
        className="btn-pill opacity-60 cursor-default flex-1 text-center"
        title="Coming soon"
      >
        {sf.label} · soon
      </span>
    );
  }
  const stampedUrl = withCoupon(addUtm(sf.url, {
    campaign: `shop-${product.id}`,
    content: sf.store,
  }), product.id);
  const handleClick = () => {
    trackEvent("shop_buy_click", {
      product_slug: product.id,
      product_kind: product.kind,
      store: sf.store,
      outbound_url: stampedUrl,
      title_id: product.id,
    });
  };
  return (
    <a
      href={stampedUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className="btn-shop"
      data-track="shop-storefront-out"
      data-store={sf.store}
    >
      {sf.label}
      <ExternalLink size={11} strokeWidth={1.5} />
    </a>
  );
}

function ProductCard({ product, index }) {
  const isExternalStorefronts =
    Array.isArray(product.external_storefronts) &&
    product.external_storefronts.length > 0;

  const openLSCheckout = () => {
    trackEvent("shop_buy_click", {
      product_slug: product.id,
      product_kind: product.kind,
      store: "dharmaweave_ls",
      price_usd: product.price_usd,
    });
    openCheckout(product);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{
        duration: 0.85,
        delay: (index % 2) * 0.07,
        ease: [0.22, 1, 0.36, 1],
      }}
      className="group"
    >
      <Link to={`/shop/${product.id}`} className="block">
        <div className={`card-shrine rounded-sm overflow-hidden ${product.orientation === 'landscape' ? 'aspect-[3/2]' : 'aspect-[4/5]'} relative bg-shrine-shadow`}>
          {product.hero_image ? (
            <img
              src={product.hero_image}
              alt={product.title}
              className="w-full h-full object-cover transition-transform duration-1000 ease-breath group-hover:scale-[1.02]"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-cream/30 font-serif italic">
              no image
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-shrine-shadow/95 via-shrine-shadow/55 to-transparent px-5 py-5 text-cream">
            <p className="font-body text-[0.7rem] text-cream/65 tracking-widest2 uppercase mb-1">
              {product.subtitle}
            </p>
            <h3 className="font-display text-xl md:text-2xl tracking-tightish leading-tight">
              {product.title}
            </h3>
          </div>
        </div>
      </Link>
      <p className="font-serif text-ink-soft text-base leading-relaxed mt-5 max-w-prose">
        {product.description}
      </p>

      {/* Buy options — flat, compact rows. For pieces with both Pure Art + Museum
          Edition versions, both rows are shown so the buyer can pick directly
          from the tile. */}
      {isExternalStorefronts ? (
        <div className="mt-4 space-y-2">
          {product.external_storefronts.map((sf, i) => (
            <div key={sf.store} className="flex items-center gap-3 flex-wrap">
              <StorefrontButton product={product} sf={sf} index={i} />
              <div className="font-body text-[0.7rem] text-ink-mute leading-tight min-w-0">
                <span className="text-ink">{sf.price_text}</span>
                <span className="mx-1.5 text-ink-mute/50">·</span>
                <span>{sf.sublabel}</span>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <button
          onClick={openLSCheckout}
          className="inline-flex items-center gap-2 mt-4 btn-pill btn-pill-solid"
        >
          <ShoppingBag size={14} />
          Buy — ${product.price_usd?.toFixed(2)}
        </button>
      )}
    </motion.div>
  );
}

function ComingSoon() {
  return (
    <section className="bg-cream py-16 md:py-24">
      <div className="max-w-3xl mx-auto px-5 md:px-10 lg:px-14 text-center">
        <p className="eyebrow mb-4">First drop coming</p>
        <h2 className="font-display text-3xl md:text-5xl text-ink leading-tight tracking-tightish">
          Sacred art for your screens and walls.
        </h2>
        <p className="font-serif text-ink-soft text-lg md:text-xl mt-6 leading-relaxed">
          Painterly wallpapers in the colors of the deity you love. High-resolution
          prints in a size that holds the room. Devotional, dignified, never neon.
        </p>
        <p className="font-serif italic text-ink-mute text-base mt-4">
          We'll write when the first pieces are ready.
        </p>
        <div className="mt-10 max-w-md mx-auto text-left">
          <EmailCapture
            titleId="shop"
            slug="shop"
            prompt="Tell me when the shop opens."
            success="We'll write the moment it's live. Thank you."
          />
        </div>
      </div>
    </section>
  );
}


// PUBLIC COPY FOR FILMS — because products.description is written for the engine.
//
// Every film row's description in `products` is internal strategy text. The
// Ox-Herding row reads "First film born into CHAKRA. Zen ox-herding sequence,
// docutainment register."; the other two discuss YPP watch-hours and paid
// acquisition. Rendering the table's description on the shop window put the
// internal system's name and its monetisation reasoning in front of readers.
//
// So the catalogue still comes from `products` — names, status, slugs, store
// links all do — but the sentence a reader sees is written for a reader. A film
// with no entry here gets no description rather than the engine's note.
const FILM_COPY = {
  film_oxherding:
    "The oldest map of the Zen path, painted frame by frame: searching for the ox, " +
    "taming it, and walking home with empty hands.",
};

// THE REST OF THE CATALOGUE.
//
// The app and the films are products this studio ships and the shop window
// never showed. They are not prints, so they get their own quiet band rather
// than a tile pretending to be one — but every link here is tagged and counted
// exactly like the prints above.
//
// The film still in production gets the magnet instead of a buy button, which
// is the only honest CTA for a thing that does not exist yet: it converts
// interest into an owned asset instead of into a dead link.
function StudioBand() {
  const app = getApp();
  const films = getFilms();
  const upcoming = films.filter((f) => f.status !== "published");
  const offer = activeOffer();
  const magnetHref = goPath("magnet-bio-instagram") || "/offer";

  const appIds = app?.platform_ids || {};

  return (
    <section className="bg-parchment/40 border-t border-dharma-gold/20 py-16 md:py-24">
      <div className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14">
        <p className="eyebrow mb-4">Also from the studio</p>
        <h2 className="font-display text-3xl md:text-5xl text-ink tracking-tightish max-w-3xl leading-[1.1]">
          The practice, and the films it feeds.
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mt-10 md:mt-14">
          {app && (
            <div className="card-shrine rounded-sm p-7 md:p-9">
              <p className="font-body text-[0.7rem] text-ink-mute tracking-widest2 uppercase">
                Free app · iOS and Android
              </p>
              <h3 className="font-display text-2xl md:text-3xl text-ink leading-tight tracking-tightish mt-3">
                {app.name}
              </h3>
              <p className="font-serif text-ink-soft text-base md:text-lg leading-relaxed mt-4 max-w-prose">
                Mantras, the Gita, and a daily practice that keeps its own count.
                Nothing to subscribe to.
              </p>
              <div className="flex flex-wrap gap-2.5 mt-6">
                <TaggedOutbound
                  url={appIds.appstore_url}
                  label="App Store"
                  event="app_store_click"
                  store="appstore"
                  titleId={app.slug}
                  slug={app.slug}
                  campaign="shop-dharma-app"
                  className="btn-shop"
                  showIcon={false}
                />
                <TaggedOutbound
                  url={appIds.play_url}
                  label="Google Play"
                  event="app_store_click"
                  store="play"
                  titleId={app.slug}
                  slug={app.slug}
                  campaign="shop-dharma-app"
                  className="btn-shop"
                  showIcon={false}
                />
              </div>
            </div>
          )}

          {upcoming.map((f) => (
            <div key={f.id} className="card-shrine rounded-sm p-7 md:p-9">
              <p className="font-body text-[0.7rem] text-saffron-fire tracking-widest2 uppercase">
                In production
              </p>
              <h3 className="font-display text-2xl md:text-3xl text-ink leading-tight tracking-tightish mt-3">
                {f.name}
              </h3>
              {FILM_COPY[f.slug] && (
                <p className="font-serif text-ink-soft text-base md:text-lg leading-relaxed mt-4 max-w-prose">
                  {FILM_COPY[f.slug]}
                </p>
              )}
              <a
                href={magnetHref}
                onClick={() =>
                  trackEvent("magnet_link_click", {
                    title_id: offer.sku,
                    slug: offer.id,
                    offer_id: offer.id,
                    surface: "shop",
                    from_product: f.slug,
                  })
                }
                className="btn-pill mt-6"
              >
                See it first
                <ArrowRight size={13} />
              </a>
            </div>
          ))}

          <div className="card-shrine rounded-sm p-7 md:p-9">
            <p className="font-body text-[0.7rem] text-ink-mute tracking-widest2 uppercase">
              Released films
            </p>
            <h3 className="font-display text-2xl md:text-3xl text-ink leading-tight tracking-tightish mt-3">
              Long-form sacred films.
            </h3>
            <p className="font-serif text-ink-soft text-base md:text-lg leading-relaxed mt-4 max-w-prose">
              Meditation-paced, painterly, anchored to a verse. Free to watch,
              always.
            </p>
            <Link
              to="/watch"
              onClick={() =>
                trackEvent("product_open", {
                  title_id: "film",
                  slug: "film",
                  surface: "shop",
                  kind: "film",
                })
              }
              className="btn-pill mt-6"
            >
              Watch them
              <ArrowRight size={13} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Shop() {
  const hasProducts = digitalProducts && digitalProducts.length > 0;

  useEffect(() => {
    trackEvent("view_content", {
      title_id: "shop",
      slug: "shop",
      surface: "shop",
      item_count: digitalProducts?.length || 0,
    });
  }, []);

  return (
    <PageShell title="Shop">
      <PageHeader
        eyebrow="Shop"
        title="Wallpapers and prints, woven with light."
        lede="Painterly devotional art — in the colors of the tradition. Single-purchase, instant download. No subscription, no clutter."
      />
      <PromoRibbon where="shop" />
      {hasProducts ? (
        <section className="py-12 md:py-16">
          <div className="max-w-8xl mx-auto px-5 md:px-10 lg:px-14">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
              {digitalProducts.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      ) : (
        <ComingSoon />
      )}
      <StudioBand />
    </PageShell>
  );
}
